import React, { useState } from "react";
import cn from "classnames";
import styles from "./ContractInformation.module.sass";
import Item from "../Item";
import Icon from "../../../components/Icon";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Switch from "../../../components/Switch";


// Options pour les listes déroulantes
const CONTRACT_TYPES = [
  "11 - Premier contrat d'apprentissage de l'apprenti",
  "21 - Nouveau contrat avec un apprenti qui a terminé son précédent contrat auprès d'un même employeur",
  "22 - Nouveau contrat avec un apprenti qui a terminé son précédent contrat auprès d'un autre employeur",
  "23 - Nouveau contrat avec un apprenti dont le précédent contrat auprès d'un autre employeur a été rompu",
  "31 - Modification de la situation juridique de l'employeur",
  "32 - Changement d'employeur dans le cadre d'un contrat saisonnier",
  "33 - Prolongation du contrat suite à un échec à l'examen de l'apprenti",
  "34 - Prolongation du contrat à la reconnaissance de l'apprenti comme travailleur handicapé",
  "35 - Modification du diplôme préparé par l'apprenti",
  "36 - Autres changements, changement de maitre d'apprentissage, de durée de travail hebdomadaire, réduction de durée, etc .",
  "37 - Modification du lieu d'execution du contrat",
  "38 - Modification du lieu principal de réalisation de la formation théorique"
];

const DEROGATION_TYPES = [
  "11 - Age de l'apprenti inférieur à 16 ans",
  "12 - Age supérieur à 29 ans, cas spécifiques prévus dnas le ccode du travail",
  "21 - Réduction de la durée du contrat ou de la période d'apprentissage",
  "22 - Allongement de la durée du contrat ou de la période d'apprentissage",
  "50 - Cumule de dérogations",
  "60 - Autre dérogation"
];

const MODE_CONTRACTUEL_TYPES = [
  "1 - A durée limitée",
  "2 - Dans le cadre d'un CDI",
  "3 - Entreprise de travail temporaire",
  "4 - Activité saisonnière à deux employeurs"
];

const settings = [
  {
    id: 0,
    title: "Travail dangereux",
  },
];

const ContractInformation = ({ className, onDetailsChange, selectedStudent }) => {
  // Debug: vérifier si selectedStudent arrive bien
  console.log('ContractInformation - selectedStudent:', selectedStudent);
  const [contractType, setContractType] = useState('');
  const [derogationType, setDerogationType] = useState('');
  const [modeContractuelType, setModeContractuelType] = useState('');
  const [previousContractNumber, setPreviousContractNumber] = useState('');
  const [selectedFilters, setSelectedFilters] = useState([]);

  // États pour les dates et autres champs
  const [trainingStartDate, setTrainingStartDate] = useState('');
  const [trainingEndDate, setTrainingEndDate] = useState('');
  const [startDateOfContract, setStartDateOfContract] = useState('');
  const [endDateOfContract, setEndDateOfContract] = useState('');
  const [workHours, setWorkHours] = useState('35');
  const [workMinutes, setWorkMinutes] = useState('0');
  const [signaturePlace, setSignaturePlace] = useState('');
  const [firstEquipmentCost, setFirstEquipmentCost] = useState('500');
  const [mobilityCost, setMobilityCost] = useState('0');
  const [mealsCount, setMealsCount] = useState('0');
  const [nightsCount, setNightsCount] = useState('0');
  const [isDangerousWork, setIsDangerousWork] = useState(false);
  const [needsSocialSupport, setNeedsSocialSupport] = useState(false);
  const [rqthMajoration, setRqthMajoration] = useState('4000');

  // États pour les erreurs de validation
  const [dateErrors, setDateErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Vérifier si le type de contrat nécessite un numéro de contrat précédent
  const needsPreviousContractNumber = contractType && !contractType.startsWith('11 -');

  // Fonction de validation des dates
  const validateDates = () => {
    const errors = {};
    
    if (trainingStartDate && trainingEndDate && startDateOfContract && endDateOfContract) {
      const trainingStart = new Date(trainingStartDate);
      const trainingEnd = new Date(trainingEndDate);
      const contractStart = new Date(startDateOfContract);
      const contractEnd = new Date(endDateOfContract);
      
      // Règle 1: Date de début du contrat ≤ Date de début de formation + 3 mois
      const maxContractStart = new Date(trainingStart);
      maxContractStart.setMonth(maxContractStart.getMonth() + 3);
      
      if (contractStart > maxContractStart) {
        errors.startDateOfContract = 'La date de début du contrat ne peut pas être supérieure à 3 mois après le début de formation';
      }
      
      // Règle 2: Date de début de formation ≥ Date de début du contrat
      if (trainingStart < contractStart) {
        errors.trainingStartDate = 'La date de début de formation doit être antérieure ou égale à la date de début du contrat';
      }
      
      // Règle 3: Date de fin de formation ≤ Date de fin du contrat
      if (trainingEnd > contractEnd) {
        errors.trainingEndDate = 'La date de fin de formation doit être antérieure ou égale à la date de fin du contrat';
      }
      
      // Règle 4: Date de début de formation ≤ Date de fin de formation
      if (trainingStart > trainingEnd) {
        errors.trainingEndDate = 'La date de fin de formation doit être postérieure à la date de début';
      }
      
      // Règle 5: Date de début du contrat ≤ Date de fin du contrat
      if (contractStart > contractEnd) {
        errors.endDateOfContract = 'La date de fin du contrat doit être postérieure à la date de début';
      }
    }
    
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fonction de validation des champs numériques
  const validateFields = () => {
    const errors = {};
    
    // Validation des heures de travail
    const hours = parseInt(workHours) || 0;
    const minutes = parseInt(workMinutes) || 0;
    
    if (hours < 20 || hours > 39) {
      errors.workHours = 'Les heures de travail doivent être comprises entre 20h et 39h par semaine';
    }
    
    if (minutes < 0 || minutes > 59) {
      errors.workMinutes = 'Les minutes doivent être comprises entre 0 et 59';
    }
    
    // Validation du temps total (heures + minutes ne doit pas dépasser 39h)
    const totalMinutes = (hours * 60) + minutes;
    if (totalMinutes > (39 * 60)) {
      errors.workMinutes = 'La durée totale ne peut pas dépasser 39h par semaine';
    }
    
    // Validation des coûts
    const equipmentCost = parseFloat(firstEquipmentCost) || 0;
    if (equipmentCost < 0 || equipmentCost > 500) {
      errors.firstEquipmentCost = 'Le coût du premier équipement doit être entre 0€ et 500€';
    }
    
    // Validation du coût de mobilité
    const mobility = parseFloat(mobilityCost) || 0;
    if (mobility < 0) {
      errors.mobilityCost = 'Le coût de mobilité ne peut pas être négatif';
    }
    
    // Validation des quantités
    const meals = parseInt(mealsCount) || 0;
    if (meals < 0 || meals > 50) {
      errors.mealsCount = 'Le nombre de repas doit être entre 0 et 50 par mois';
    }
    
    const nights = parseInt(nightsCount) || 0;
    if (nights < 0 || nights > 30) {
      errors.nightsCount = 'Le nombre de nuits doit être entre 0 et 30 par mois';
    }
    
    // Majoration RQTH retirée selon les nouvelles spécifications
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fonction pour envoyer les données au composant parent
  const updateParent = () => {
    if (typeof onDetailsChange === 'function') {
      // Extraire les codes numériques des valeurs sélectionnées
      const contractTypeCode = contractType ? contractType.split(' - ')[0] : '';
      const derogationTypeCode = derogationType ? derogationType.split(' - ')[0] : '';
      const modeContractuelTypeCode = modeContractuelType ? modeContractuelType.split(' - ')[0] : '';

      console.log('ContractInformation - Transmission des données:', { workHours, workMinutes });
      
      onDetailsChange({
        contractType: contractTypeCode,
        contractTypeText: contractType,
        derogationType: derogationTypeCode,
        derogationTypeText: derogationType,
        modeContractuelType: modeContractuelTypeCode,
        modeContractuelTypeText: modeContractuelType,
        previousContractNumber,
        trainingStartDate,
        trainingEndDate,
        startDateOfContract,
        endDateOfContract,
        workHours,
        workMinutes,
        isDangerousWork,
        firstEquipmentCost,
        mobilityCost,
        mealsCount,
        nightsCount,
        needsSocialSupport,
        rqthMajoration
      });
    }
  };

  // Transmettre les valeurs par défaut au montage du composant
  React.useEffect(() => {
    updateParent();
  }, []); // Se déclenche uniquement au montage

  // Mettre à jour le parent à chaque changement d'état
  React.useEffect(() => {
    // Valider les dates et les champs avant de mettre à jour le parent
    const datesValid = validateDates();
    const fieldsValid = validateFields();
    updateParent();
  }, [
    contractType, derogationType, modeContractuelType, previousContractNumber,
    trainingStartDate, trainingEndDate, startDateOfContract, endDateOfContract,
    workHours, workMinutes, isDangerousWork, firstEquipmentCost,
    mobilityCost, mealsCount, nightsCount, needsSocialSupport, rqthMajoration
  ]);

  const handleChange = (id) => {
    if (id === 0) {
      setIsDangerousWork(!isDangerousWork);
    } else if (id === 1) {
      setNeedsSocialSupport(!needsSocialSupport);
    } else {
      if (selectedFilters.includes(id)) {
        setSelectedFilters(selectedFilters.filter((x) => x !== id));
      } else {
        setSelectedFilters((selectedFilters) => [...selectedFilters, id]);
      }
    }
  };


  return (
    <Item
      className={cn(styles.card, className)}
      title="Contrat"
      classTitle="title-green"
    >
      <div className={styles.fieldset}>

        <Dropdown
          className={styles.field}
          label="Type de contrat"
          name="contract-type"
          options={CONTRACT_TYPES}
          setValue={(value) => {
            setContractType(value);
            // Réinitialiser le numéro de contrat précédent si on choisit un contrat initial
            if (value.startsWith('11 -')) {
              setPreviousContractNumber('');
            }
          }}
          value={contractType}
          placeholder="Sélectionner..."
          required
        />

        {needsPreviousContractNumber && (
          <TextInput
            className={styles.field}
            label="Numéro du contrat précédent"
            name="previous-contract-number"
            type="text"
            placeholder="Saisir le numéro du contrat précédent"
            value={previousContractNumber}
            onChange={(e) => setPreviousContractNumber(e.target.value)}
            tooltip="Le numéro de contrat précédent correspondant au numéro de dépôt du dossier"
            required
          />
        )}

        <Dropdown
          className={styles.field}
          label="Type de dérogation"
          name="derogation-type"
          options={DEROGATION_TYPES}
          setValue={setDerogationType}
          value={derogationType}
          placeholder="Sélectionner..."
          required
        />

        <Dropdown
          className={styles.field}
          label="Mode contractuel de l'apprentissage"
          name="mode-contractuel-type"
          options={MODE_CONTRACTUEL_TYPES}
          setValue={setModeContractuelType}
          value={modeContractuelType}
          placeholder="Sélectionner..."
          required
        />

        <div className={styles.divider} />


        <div className={styles.group}>
          <div className={styles.field}>
            <TextInput
              label="Date de début de formation"
              name="training_start_date"
              type="date"
              value={trainingStartDate}
              onChange={(e) => setTrainingStartDate(e.target.value)}
              tooltip="Date à laquelle l'apprenti commence sa formation théorique au centre de formation"
              required
            />
            {dateErrors.trainingStartDate && (
              <div className={styles.errorMessage}>{dateErrors.trainingStartDate}</div>
            )}
          </div>
          <div className={styles.field}>
            <TextInput
              label="Date de fin de formation"
              name="training_end_date"
              type="date"
              value={trainingEndDate}
              onChange={(e) => setTrainingEndDate(e.target.value)}
              tooltip="Date à laquelle l'apprenti termine sa formation théorique au centre de formation"
              required
            />
            {dateErrors.trainingEndDate && (
              <div className={styles.errorMessage}>{dateErrors.trainingEndDate}</div>
            )}
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.field}>
            <TextInput
              label="Date de début du contrat"
              name="start_date_of_contract"
              type="date"
              value={startDateOfContract}
              onChange={(e) => setStartDateOfContract(e.target.value)}
              tooltip="Date à laquelle l'apprenti commence son travail pratique en entreprise"
              required
            />
            {dateErrors.startDateOfContract && (
              <div className={styles.errorMessage}>{dateErrors.startDateOfContract}</div>
            )}
          </div>
          <div className={styles.field}>
            <TextInput
              label="Date de fin du contrat"
              name="end_date_of_contract"
              type="date"
              value={endDateOfContract}
              onChange={(e) => setEndDateOfContract(e.target.value)}
              tooltip="Date à laquelle le contrat d'apprentissage se termine en entreprise"
              required
            />
            {dateErrors.endDateOfContract && (
              <div className={styles.errorMessage}>{dateErrors.endDateOfContract}</div>
            )}
          </div>
        </div>


        <div className={styles.divider} />

        <div className={styles.subtitle}>Durée hebdomadaire du travail</div>

        <div className={styles.group}>
          <div className={styles.field}>
            <TextInput
              label="Heures"
              name="work_hours"
              type="number"
              placeholder="35"
              value={workHours}
              onChange={(e) => {
                const value = Math.max(0, parseInt(e.target.value) || 0);
                setWorkHours(value.toString());
              }}
              tooltip="Nombre d'heures de travail par semaine (entre 20h et 39h)"
              min="20"
              max="39"
            />
            {fieldErrors.workHours && (
              <div className={styles.errorMessage}>{fieldErrors.workHours}</div>
            )}
          </div>
          <div className={styles.field}>
            <TextInput
              label="Minutes"
              name="work_minutes"
              type="number"
              placeholder="0"
              value={workMinutes}
              onChange={(e) => {
                const value = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                setWorkMinutes(value.toString());
              }}
              tooltip="Minutes supplémentaires (0-59)"
              min="0"
              max="59"
            />
            {fieldErrors.workMinutes && (
              <div className={styles.errorMessage}>{fieldErrors.workMinutes}</div>
            )}
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.line}>
            <div className={styles.title}>
              Travail dangereux
            </div>
            <Switch
              className={styles.switch}
              value={isDangerousWork}
              onChange={() => handleChange(0)}
            />
          </div>
        </div>

        <div className={styles.subtitle}>Coûts et avantages</div>

        <div className={styles.group}>
          <div className={styles.field}>
            <TextInput
              label="Coût du premier équipement (€)"
              name="first_equipment_cost"
              type="number"
              step="0.01"
              placeholder="500"
              value={firstEquipmentCost}
              onChange={(e) => {
                const value = Math.max(0, Math.min(500, parseFloat(e.target.value) || 0));
                setFirstEquipmentCost(value.toString());
              }}
              tooltip="Coût des équipements nécessaires pour la formation (max 500€)"
              min="0"
              max="500"
            />
            {fieldErrors.firstEquipmentCost && (
              <div className={styles.errorMessage}>{fieldErrors.firstEquipmentCost}</div>
            )}
          </div>
          <div className={styles.field}>
            <TextInput
              label="Coût de mobilité (€)"
              name="mobility_cost"
              type="number"
              step="0.01"
              placeholder="0"
              value={mobilityCost}
              onChange={(e) => setMobilityCost(e.target.value)}
              tooltip="Frais de transport et de mobilité"
              min="0"
            />
            {fieldErrors.mobilityCost && (
              <div className={styles.errorMessage}>{fieldErrors.mobilityCost}</div>
            )}
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.field}>
            <TextInput
              label="Nombre de repas"
              name="meals_count"
              type="number"
              placeholder="0"
              value={mealsCount}
              onChange={(e) => {
                const value = Math.max(0, parseInt(e.target.value) || 0);
                setMealsCount(value.toString());
              }}
              tooltip="1 repas = 3€"
              min="0"
            />
            {fieldErrors.mealsCount && (
              <div className={styles.errorMessage}>{fieldErrors.mealsCount}</div>
            )}
          </div>
          <div className={styles.field}>
            <TextInput
              label="Nombre de nuits"
              name="nights_count"
              type="number"
              placeholder="0"
              value={nightsCount}
              onChange={(e) => {
                const value = Math.max(0, parseInt(e.target.value) || 0);
                setNightsCount(value.toString());
              }}
              tooltip="1 nuit = 6€"
              min="0"
            />
            {fieldErrors.nightsCount && (
              <div className={styles.errorMessage}>{fieldErrors.nightsCount}</div>
            )}
          </div>
        </div>

        <div className={styles.group}>
          <div className={styles.line}>
            <div className={styles.title}>
              Demander la majoration liée à l'accompagnement social des apprentis en difficulté dans les DROM
            </div>
            <Switch
              className={styles.switch}
              value={needsSocialSupport}
              onChange={() => handleChange(1)}
            />
          </div>
        </div>

      </div>
    </Item>
  );
};

export default ContractInformation;